import React, { useMemo } from 'react'
import { Badge, HStack, Text } from '@chakra-ui/react'
import * as diff from 'diff'

const COLOR_ADDITION = '#8fbc8f'
const COLOR_REMOVAL = '#ffd700'

interface EncoderDiffDisplayProps {
    originalBody: string
    currentValue: string
    submitterName: string | null
}

export const EncoderDiffDisplay: React.FC<EncoderDiffDisplayProps> = ({
    originalBody,
    currentValue,
    submitterName,
}) => {
    const changes = useMemo(() => {
        return diff.diffChars(originalBody, currentValue)
    }, [originalBody, currentValue])

    // Don't show anything if there are no changes
    if (changes.length <= 1) {
        return null
    }

    return (
        <>
            {/* Suggested changes badge */}
            {submitterName && (
                <Badge
                    mt="5"
                    alignSelf="flex-start"
                    variant="outline"
                    padding="0.65rem"
                    borderRadius="5px"
                    boxShadow="none"
                    backgroundColor="#fff"
                    color="#008b8b"
                >
                    Suggested changes by{' '}
                    <Badge ml="5" padding="0.3rem 0.5rem">
                        {submitterName}
                    </Badge>
                </Badge>
            )}

            {/* Before changes section */}
            <Badge
                alignSelf="flex-start"
                variant="outline"
                padding="0.65rem"
                borderRadius="5px"
                boxShadow="none"
                backgroundColor="#fff"
                color="#008b8b"
            >
                Before changes:
            </Badge>
            <Text
                style={{
                    fontFamily: "'Barlow', monospace",
                    wordSpacing: '5px',
                    lineHeight: 2,
                }}
            >
                {originalBody}
            </Text>

            {/* Overview changes section */}
            <Badge
                padding="0.65rem"
                variant="outline"
                alignSelf="flex-start"
                borderRadius="5px"
                boxShadow="none"
                backgroundColor="#fff"
                color="#008b8b"
            >
                Overview changes:{' '}
                <Badge
                    mr="2"
                    ml="2"
                    background={COLOR_ADDITION}
                    padding="0.2rem 0.75rem"
                >
                    Added
                </Badge>
                <Badge background={COLOR_REMOVAL} padding="0.2rem 0.75rem">
                    Removed
                </Badge>
            </Badge>

            {/* Diff visualization */}
            <HStack
                spacing={0}
                style={{
                    fontFamily: "'Barlow', monospace",
                    wordSpacing: '5px',
                    lineHeight: 2,
                }}
                display="inline-block"
            >
                {changes.map((change, index) => {
                    if (change.removed) {
                        return (
                            <Text
                                key={index}
                                as="span"
                                style={{
                                    background: COLOR_REMOVAL,
                                    borderRadius: '3px',
                                }}
                            >
                                {change.value}
                            </Text>
                        )
                    }
                    if (change.added) {
                        return (
                            <Text
                                key={index}
                                as="span"
                                style={{
                                    background: COLOR_ADDITION,
                                    borderRadius: '3px',
                                }}
                            >
                                {change.value}
                            </Text>
                        )
                    }
                    return (
                        <Text key={index} as="span">
                            {change.value}
                        </Text>
                    )
                })}
            </HStack>
        </>
    )
}
